import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { createPublicRocClient } from '@/adapters/publicROCApi';
import { FormLoading } from '@/components/FormLoading';
import { ChatInput } from '@/components/roc/ChatInput';
import type { RocDocumentDownloadArgs } from '@/components/roc/DocumentMessageCard';
import { useSnackbar } from '@/hooks/useSnackbar';
import { OutboundStatusChip } from '@/pages/CRM/ReferralOutbound/components/OutboundDataGrid/OutboundDataGridColumns';
import ConversationMessagesPanel from '@/pages/CRM/ReferralOutbound/ReferralManagement/components/ConversationMessagesPanel';
import {
  DocumentsTab,
  type DocumentsTabDocument
} from '@/pages/CRM/ReferralOutbound/ReferralManagement/components/DocumentsTab';
import {
  type ReadState,
  shouldMarkAsRead
} from '@/pages/CRM/ReferralOutbound/ReferralManagement/lib/readReceipts';
import type { ReadReceipt } from '@/types/roc/models/ReadReceipt';
import type { RecipientStatus } from '@/types/roc/models/RecipientStatus';
import { RecipientStatus as RecipientStatusEnum } from '@/types/roc/models/RecipientStatus';
import { ResponseError } from '@/types/roc/runtime';

import {
  getPublicReferralAccessErrorMessage,
  type PublicReferralAccessData,
  usePublicReferralAccess
} from './hooks/usePublicReferralAccess';
import {
  usePublicReferralConversation,
  usePublicReferralMessages,
  usePublicSendReferralMessageMutation
} from './hooks/usePublicReferralMessages';

import {
  getPublicReferralAccessQueryKey,
  getPublicReferralDocumentsQueryKey,
  getPublicReferralMessagesQueryKey
} from './queryKeys';

const PUBLIC_REFERRAL_TABS = ['chat', 'documents'] as const;

type PublicReferralTab = (typeof PUBLIC_REFERRAL_TABS)[number];

const STATUS_ACTIONS: Array<{ label: string; status: RecipientStatus }> = [
  { label: 'Accept', status: RecipientStatusEnum.Accepted },
  { label: 'Decline', status: RecipientStatusEnum.Declined },
  { label: 'Need More Info', status: RecipientStatusEnum.NeedsMoreInfo }
];

function isPublicReferralTab(value: unknown): value is PublicReferralTab {
  return PUBLIC_REFERRAL_TABS.includes(value as PublicReferralTab);
}

function getMessageFromError(error: unknown, fallback: string) {
  if (error instanceof ResponseError) {
    if (error.response.status === 401 || error.response.status === 403) {
      return 'This referral link is invalid or no longer available.';
    }
  }

  return fallback;
}

function formatDob(dob: Date | null | undefined) {
  if (!dob || Number.isNaN(dob.getTime())) {
    return '—';
  }

  return dayjs(dob).format('MMM D, YYYY');
}

function computePublicReadState(
  messageId: string,
  readReceipts: ReadReceipt[] | undefined,
  senderFacilityId: string | undefined,
  recipientFacilityId: string | undefined
): ReadState | null {
  if (!readReceipts || !senderFacilityId || !recipientFacilityId) {
    return { firstReadAt: null };
  }

  const eligible = readReceipts.filter(
    (receipt) =>
      receipt.user_facility_id === senderFacilityId &&
      receipt.recipient_facility_id === recipientFacilityId &&
      receipt.last_read_message_id >= messageId
  );

  if (eligible.length === 0) {
    return { firstReadAt: null };
  }

  const firstReadAt = eligible.map((receipt) => receipt.last_read_at).sort()[0];
  return { firstReadAt: firstReadAt ?? null };
}

export default function PublicReferralAccessPage() {
  const { token } = useParams<{ token: string }>();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<PublicReferralTab>('chat');
  const [pendingStatus, setPendingStatus] = useState<RecipientStatus | null>(
    null
  );
  const [chatInputKey, setChatInputKey] = useState(0);
  const latestAccessQueryKeyRef = useRef<ReturnType<
    typeof getPublicReferralAccessQueryKey
  > | null>(null);
  const latestMessagesQueryKeyRef = useRef<ReturnType<
    typeof getPublicReferralMessagesQueryKey
  > | null>(null);
  const latestDocumentsQueryKeyRef = useRef<ReturnType<
    typeof getPublicReferralDocumentsQueryKey
  > | null>(null);

  const accessQuery = usePublicReferralAccess(token);
  const referralId = accessQuery.data?.referral.referral_id;
  const recipientFacilityId = accessQuery.data?.referral.recipient.facility_id;
  const senderFacilityId = accessQuery.data?.referral.sender_facility_id;

  const messagesQuery = usePublicReferralMessages(token, referralId);
  const sendMessageMutation = usePublicSendReferralMessageMutation(
    token,
    referralId
  );
  const accessQueryKey = useMemo(
    () => getPublicReferralAccessQueryKey(token),
    [token]
  );
  const messagesQueryKey = useMemo(
    () => getPublicReferralMessagesQueryKey(token, referralId),
    [token, referralId]
  );
  const documentsQueryKey = useMemo(
    () => getPublicReferralDocumentsQueryKey(token, referralId),
    [token, referralId]
  );

  latestAccessQueryKeyRef.current = accessQueryKey;
  latestMessagesQueryKeyRef.current = messagesQueryKey;
  latestDocumentsQueryKeyRef.current = documentsQueryKey;

  const documentsQuery = useQuery({
    queryKey: documentsQueryKey,
    queryFn: async () => {
      if (!token || !referralId) {
        throw new Error('Missing referral context.');
      }

      const client = createPublicRocClient(token);
      const response = await client.listDocuments({
        referralId,
        limit: 100
      });

      return response.items;
    },
    enabled: Boolean(token && referralId),
    retry: false
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: RecipientStatus) => {
      if (!token || !referralId) {
        throw new Error('Missing referral context.');
      }

      const client = createPublicRocClient(token);
      return client.patchRecipientStatus({
        referralId,
        updateRecipientStatusRequest: { status }
      });
    },
    onSuccess: (recipient) => {
      queryClient.setQueryData(
        accessQueryKey,
        (current: PublicReferralAccessData | undefined) =>
          current
            ? {
                ...current,
                referral: {
                  ...current.referral,
                  recipient: {
                    ...current.referral.recipient,
                    status: recipient.status
                  }
                }
              }
            : current
      );

      queryClient.invalidateQueries({
        queryKey: messagesQueryKey
      });
    }
  });

  const markAsReadMutation = useMutation({
    mutationFn: ({
      referralId,
      recipientFacilityId,
      lastReadMessageId
    }: {
      referralId: string;
      recipientFacilityId: string;
      lastReadMessageId: string;
    }) => {
      if (!token) {
        throw new Error('Missing referral access token.');
      }

      const client = createPublicRocClient(token);
      return client.upsertReadReceipt({
        referralId,
        recipientFacilityId,
        updateReadReceiptRequest: {
          last_read_message_id: lastReadMessageId
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messagesQueryKey
      });
    }
  });

  const conversationMessages = usePublicReferralConversation(
    messagesQuery.data?.messages,
    senderFacilityId,
    recipientFacilityId
  );

  const latestRecipientSentMessageId = useMemo(() => {
    if (!recipientFacilityId) {
      return null;
    }

    for (let index = conversationMessages.length - 1; index >= 0; index -= 1) {
      const message = conversationMessages[index];
      if (
        message &&
        !message.isSystemEvent() &&
        message.receiver_facility_id &&
        message.sender_facility_id === recipientFacilityId
      ) {
        return message.message_id;
      }
    }

    return null;
  }, [conversationMessages, recipientFacilityId]);

  const latestConversationMessage = useMemo(
    () =>
      conversationMessages.length > 0
        ? (conversationMessages[conversationMessages.length - 1] ?? null)
        : null,
    [conversationMessages]
  );
  const markAsRead = markAsReadMutation.mutate;

  useEffect(() => {
    return () => {
      const latestAccessQueryKey = latestAccessQueryKeyRef.current;
      const latestMessagesQueryKey = latestMessagesQueryKeyRef.current;
      const latestDocumentsQueryKey = latestDocumentsQueryKeyRef.current;

      if (latestAccessQueryKey) {
        queryClient.removeQueries({
          queryKey: latestAccessQueryKey,
          exact: true
        });
      }

      if (latestMessagesQueryKey) {
        queryClient.removeQueries({
          queryKey: latestMessagesQueryKey,
          exact: true
        });
      }

      if (latestDocumentsQueryKey) {
        queryClient.removeQueries({
          queryKey: latestDocumentsQueryKey,
          exact: true
        });
      }
    };
  }, [queryClient]);

  useEffect(() => {
    if (tab !== 'chat' || !latestConversationMessage || !referralId) {
      return;
    }

    if (latestConversationMessage.sender_facility_id === recipientFacilityId) {
      return;
    }

    if (
      !shouldMarkAsRead(
        latestConversationMessage.message_id,
        messagesQuery.data?.readReceipts ?? [],
        recipientFacilityId ?? '',
        recipientFacilityId ?? ''
      )
    ) {
      return;
    }

    if (!recipientFacilityId) {
      return;
    }

    markAsRead({
      referralId,
      recipientFacilityId,
      lastReadMessageId: latestConversationMessage.message_id
    });
  }, [
    latestConversationMessage,
    messagesQuery.data?.readReceipts,
    markAsRead,
    recipientFacilityId,
    referralId,
    tab
  ]);

  const handleDownloadDocument = async ({
    referralId: downloadReferralId,
    documentId
  }: RocDocumentDownloadArgs) => {
    if (!token) {
      throw new Error('Missing referral access token.');
    }

    const client = createPublicRocClient(token);
    const { url } = await client.getDocumentDownloadUrl({
      referralId: downloadReferralId,
      documentId
    });
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      throw new Error('Unexpected download URL protocol');
    }

    const anchor = document.createElement('a');
    anchor.href = parsed.toString();
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const currentFacilityName =
    accessQuery.data?.referral.recipient.facility_name ?? 'Your facility';

  const partnerFacilityName =
    accessQuery.data?.referral.sender_facility_name ?? 'Sender facility';

  const partnerLabelMap = useMemo(
    () => ({
      [senderFacilityId ?? '']: partnerFacilityName,
      [recipientFacilityId ?? '']: currentFacilityName
    }),
    [
      currentFacilityName,
      partnerFacilityName,
      recipientFacilityId,
      senderFacilityId
    ]
  );
  const publicDocuments = documentsQuery.data as
    | DocumentsTabDocument[]
    | undefined;
  const readStateByMessageId = useMemo(
    () =>
      Object.fromEntries(
        conversationMessages.map((message) => [
          message.message_id,
          message.message_id === latestRecipientSentMessageId
            ? computePublicReadState(
                message.message_id,
                messagesQuery.data?.readReceipts,
                senderFacilityId,
                recipientFacilityId
              )
            : null
        ])
      ) as Record<string, ReadState | null>,
    [
      conversationMessages,
      latestRecipientSentMessageId,
      messagesQuery.data?.readReceipts,
      recipientFacilityId,
      senderFacilityId
    ]
  );

  if (accessQuery.isLoading) {
    return <FormLoading label="Loading referral…" subLabel="" />;
  }

  if (accessQuery.isError || !accessQuery.data) {
    return (
      <Stack
        minHeight="100vh"
        alignItems="center"
        justifyContent="center"
        px={2}
        textAlign="center"
        gap={1}
      >
        <Typography variant="h6">Unable to open this referral</Typography>
        <Typography color="text.secondary">
          {getPublicReferralAccessErrorMessage(
            accessQuery.error,
            'Failed to load this referral.'
          )}
        </Typography>
      </Stack>
    );
  }

  const { referral } = accessQuery.data;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        px: { xs: 0, sm: 2, md: 3 },
        py: { xs: 0, sm: 2, md: 3 }
      }}
    >
      <Stack
        sx={{
          width: '100%',
          maxWidth: 960,
          minHeight: {
            xs: '100vh',
            sm: 'calc(100vh - 32px)',
            md: 'calc(100vh - 48px)'
          },
          mx: 'auto',
          bgcolor: 'background.paper',
          borderRadius: { xs: 0, sm: 1.5 },
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        <Stack spacing={2.5} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            gap={1.5}
          >
            <Stack gap={0.5}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Referral from {partnerFacilityName}
              </Typography>
              <Typography
                sx={{ fontSize: 24, fontWeight: 700, color: 'text.primary' }}
              >
                {[referral.first_name, referral.last_name]
                  .filter(Boolean)
                  .join(' ') || 'Patient'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                DOB: {formatDob(referral.dob)}
              </Typography>
            </Stack>
            <OutboundStatusChip status={referral.recipient.status} />
          </Stack>

          <Stack direction="row" flexWrap="wrap" gap={1}>
            {STATUS_ACTIONS.map((action) => {
              const isActive = referral.recipient.status === action.status;
              return (
                <Button
                  key={action.status}
                  variant={isActive ? 'contained' : 'outlined'}
                  size="small"
                  disabled={updateStatusMutation.isLoading}
                  onClick={() => setPendingStatus(action.status)}
                  sx={{ flexGrow: { xs: 1, sm: 0 } }}
                >
                  {action.label}
                </Button>
              );
            })}
          </Stack>
        </Stack>

        <Divider />

        <Stack flex={1} sx={{ minHeight: 0, position: 'relative' }}>
          {(sendMessageMutation.isLoading ||
            updateStatusMutation.isLoading) && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1
              }}
            />
          )}

          <Tabs
            value={tab}
            onChange={(_event, value) => {
              if (isPublicReferralTab(value)) {
                setTab(value);
              }
            }}
            variant="fullWidth"
            sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
          >
            <Tab
              label="Chat"
              value="chat"
              sx={{ minHeight: 44, fontSize: 13 }}
            />
            <Tab
              label="Documents"
              value="documents"
              sx={{ minHeight: 44, fontSize: 13 }}
            />
          </Tabs>

          {tab === 'chat' && (
            <>
              <ConversationMessagesPanel
                messages={conversationMessages}
                resetKey={`${referralId ?? ''}:${recipientFacilityId ?? ''}`}
                senderFacilityId={recipientFacilityId ?? ''}
                senderFacilityName={currentFacilityName}
                getFacilityName={(facilityId) =>
                  partnerLabelMap[facilityId] ?? facilityId
                }
                readStateByMessageId={readStateByMessageId}
                onOpenDocument={handleDownloadDocument}
                isLoading={messagesQuery.isLoading}
                isError={messagesQuery.isError}
              />

              <ChatInput
                key={chatInputKey}
                onSend={async (content) => {
                  try {
                    await sendMessageMutation.mutateAsync(content);
                    setChatInputKey((current) => current + 1);
                  } catch (error) {
                    showSnackbar({
                      message: getMessageFromError(
                        error,
                        'Failed to send message. Please try again.'
                      ),
                      severity: 'error'
                    });
                  }
                }}
                isSending={sendMessageMutation.isLoading}
              />
            </>
          )}

          {tab === 'documents' && (
            <DocumentsTab
              referralId={referralId ?? ''}
              documents={publicDocuments ?? []}
              isLoading={documentsQuery.isLoading}
              isError={documentsQuery.isError}
              onOpenDocument={handleDownloadDocument}
            />
          )}
        </Stack>
      </Stack>

      <Dialog
        open={pendingStatus !== null}
        onClose={() => setPendingStatus(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm status change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Update this referral to{' '}
            {pendingStatus
              ? (STATUS_ACTIONS.find(
                  (action) => action.status === pendingStatus
                )?.label ?? pendingStatus)
              : ''}
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStatus(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!pendingStatus || updateStatusMutation.isLoading}
            onClick={async () => {
              if (!pendingStatus) {
                return;
              }

              try {
                await updateStatusMutation.mutateAsync(pendingStatus);
                setPendingStatus(null);
              } catch (error) {
                showSnackbar({
                  message: getMessageFromError(
                    error,
                    'Failed to update referral status.'
                  ),
                  severity: 'error'
                });
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
